import { YesNo } from "../lib/models/enums";
import { RateLock } from "../lib/models/rate-lock.model";
import { RentalCalculation } from "../lib/models/rental/rental-calculation.model";
import {
  RentalOverrideOptions,
  RentalPremierQuote,
  RentalQuote,
  Scenario,
} from "../lib/models/rental/rental.model";
import { FixAndFlipCalculation } from "../lib/models/short-term/short-term-calculation.model";
import {
  FixAndFlipOverrideOptions,
  FixAndFlipQuote,
} from "../lib/models/short-term/short-term.model";
import { QuoteCalculationStatus } from "../lib/types/QuoteCalculationStatus";
import { QuoteMetadata } from "../lib/types/QuoteMetadata";
import { QuoteType } from "../lib/types/QuoteType";
import { QuoteState } from "./quote-state";

export type QuotingType =
  | FixAndFlipQuote
  | RentalQuote
  | RentalPremierQuote
  | null;

export type CalculationType = FixAndFlipCalculation | RentalCalculation | null;

export type QuoteActionTypes =
  | { type: "QUOTE_STATUS_UPDATED"; payload: boolean }
  | { type: "QUOTE_LOADING" }
  | {
      type: "QUOTE_LOADED";
      payload: {
        quote: QuotingType;
        calculation: CalculationType;
        quoteMetadata: QuoteMetadata;
        rateLock?: RateLock;
      };
    }
  | { type: "QUOTE_LOAD_ERROR" }
  | {
      type: "QUOTE_SF_INFO_UPDATE";
      payload: {
        sfaccountid: string;
        sfdealid: string;
        campaignId: string;
        campaignName: string;
        campaignExpirationDate: string;
        campaignIncentive: string;
        campaignCode: string;
      };
    }
  | { type: "QUOTE_UPDATED"; payload: { quote: QuotingType } }
  | { type: "QUOTE_FORM_ERROR"; payload: boolean }
  | { type: "QUOTE_SAVING" }
  | {
      type: "QUOTE_SAVED";
      payload: { quote: QuotingType; calculation: CalculationType };
    }
  | { type: "QUOTE_SAVE_ERROR" }
  | { type: "QUOTE_CALCULATION_RESET" }
  | { type: "QUOTE_CALCULATION_REQUIRED" }
  | { type: "QUOTE_CALCULATING" }
  | {
      type: "QUOTE_CALCULATED";
      payload: {
        quote: QuotingType;
        calculation: CalculationType;
        hasCalculated: boolean;
      };
    }
  | { type: "QUOTE_CALCULATE_ERROR" }
  | { type: "QUOTE_CALCULATE_WARNING"; payload: QuoteCalculationStatus }
  | { type: "QUOTE_TYPE_CHANGE"; payload: QuoteType }
  | { type: "SCENARIO_UPDATE"; payload: Scenario }
  | { type: "SCENARIO_LOADED"; payload: Scenario[] }
  | { type: "QUOTE_SUBMITTED"; payload: boolean }
  | { type: "QUOTE_SUBMITTED_ERROR" }
  | { type: "QUOTE_SUBMITTING" }
  | { type: "USER_CHANGED"; payload: string };

export const quoteReducer = (
  state: QuoteState,
  action: QuoteActionTypes
): QuoteState => {
  switch (action.type) {
    case "USER_CHANGED":
      return {
        ...state,
        username: action.payload,
      };

    case "QUOTE_STATUS_UPDATED":
      return {
        ...state,
        isDirty: action.payload,
      };

    case "QUOTE_TYPE_CHANGE":
      if (state.quote !== null) {
        const hasCalculated =
          state.hasCalculated && state.quote.quoteType === action.payload;

        return {
          ...state,
          quoteType: action.payload,
          hasCalculated,
        };
      }

      return {
        ...state,
        quoteType: action.payload,
        hasCalculated: false,
      };

    case "QUOTE_LOADING":
      return { ...state, isLoading: true };

    case "QUOTE_LOADED":
      if (action.payload.quote?.quoteType === "STF") {
        const fixAndFlipCalc = action.payload
          .calculation as FixAndFlipCalculation;
        const borrowerTier = fixAndFlipCalc.BorrowerInfo.borrowerTier;
        const quote = action.payload.quote as FixAndFlipQuote;

        const overrides = new FixAndFlipOverrideOptions(
          quote.exceptionPricingOverride
        );
        quote.exceptionPricingOverride = overrides;

        if (quote && quote.borrowerInfo) {
          quote.borrowerInfo.borrowerTier = borrowerTier;
        }

        return {
          ...state,
          quote: action.payload.quote,
          quoteCalculation: fixAndFlipCalc,
          metadata: action.payload.quoteMetadata,
          isLoading: false,
          isNewQuote: false,
          hasCalculated: true,
          sfIntegration: {
            sfaccountid: action.payload.quote.sfaccountid,
            sfdealid: action.payload.quote.sfdealid,
            campaignId: action.payload.quote.campaignId,
            originalsfcampaignid: action.payload.quote.campaignId,
            campaignName: action.payload.quote.campaignName,
            campaignExpirationDate: action.payload.quote.campaignExpirationDate,
            campaignIncentive: action.payload.quote.campaignIncentive,
            campaignCode: action.payload.quote.campaignCode,
            originalsfaccountid: action.payload.quote.sfaccountid,
            originalsfdealid: action.payload.quote.sfdealid,
          },
          hasQuoteBeenSubmitted:
            action.payload.quoteMetadata.submittedToSalesforce,
          requiresQuoteResubmission: false,
        };
      } else {
        const rentalQuote = action.payload.quote as RentalQuote;

        const rentalOverrides = new RentalOverrideOptions(
          rentalQuote.exceptionPricingOverride
        );
        rentalQuote.exceptionPricingOverride = rentalOverrides;

        return {
          ...state,
          quote: action.payload.quote,
          quoteCalculation: action.payload.calculation,
          metadata: action.payload.quoteMetadata,
          isLoading: false,
          isNewQuote: false,
          hasCalculated: true,
          requiresQuoteResubmission: false,
          hasQuoteBeenSubmitted:
            action.payload.quoteMetadata.submittedToSalesforce,
          sfIntegration: {
            sfaccountid: rentalQuote.sfaccountid,
            sfdealid: rentalQuote.sfdealid,
            campaignId: rentalQuote.campaignId,
            originalsfcampaignid: rentalQuote.campaignId,
            campaignName: rentalQuote.campaignName,
            campaignExpirationDate: rentalQuote.campaignExpirationDate,
            campaignIncentive: rentalQuote.campaignIncentive,
            campaignCode: rentalQuote.campaignCode,
            originalsfaccountid: rentalQuote.sfaccountid,
            originalsfdealid: rentalQuote.sfdealid,
          },
          rentalQuoteScenarios: rentalQuote.scenario ?? [],
        };
      }

    case "QUOTE_LOAD_ERROR":
      return { ...state, isLoading: false };

    case "QUOTE_FORM_ERROR":
      const hasErrors = action.payload;

      return {
        ...state,
        hasFormErrors: hasErrors,
      };

    case "QUOTE_SF_INFO_UPDATE":
      const {
        sfaccountid: incomingSfAccount,
        sfdealid: incomingSfDeal,
        campaignId: incomingSfCampaign,
        campaignName: incomingCampaignName,
        campaignExpirationDate: incomingCampaignExpirationDate,
        campaignIncentive: incomingCampaignIncentive,
        campaignCode: incomingCampaignCode,
      } = action.payload;
      const originalAccountId = state.sfIntegration?.originalsfaccountid ?? "";
      const originalDealId = state.sfIntegration?.originalsfdealid ?? "";
      const originalCampaignId =
        state.sfIntegration?.originalsfcampaignid ?? "";
      const quoteFromState = state.quote;

      const haveSfValuesChanged = () => {
        if (!incomingSfAccount || !incomingSfDeal) {
          return true;
        }

        return (
          originalAccountId !== incomingSfAccount ||
          originalDealId !== incomingSfDeal ||
          originalCampaignId !== incomingSfCampaign
        );
      };

      const hasSfCampaignChanged = () => {
        return originalCampaignId !== incomingSfCampaign;
      };

      if (quoteFromState) {
        quoteFromState.sfaccountid = incomingSfAccount;
        quoteFromState.sfdealid = incomingSfDeal;
        quoteFromState.campaignId = incomingSfCampaign;
        quoteFromState.campaignName = incomingCampaignName;
        quoteFromState.campaignExpirationDate = incomingCampaignExpirationDate;
        quoteFromState.campaignIncentive = incomingCampaignIncentive;
        quoteFromState.campaignCode = incomingCampaignCode;
      }

      return {
        ...state,
        sfIntegration: {
          ...state.sfIntegration,
          sfaccountid: incomingSfAccount,
          sfdealid: incomingSfDeal,
          campaignId: incomingSfCampaign,
          campaignName: incomingCampaignName,
          campaignExpirationDate: incomingCampaignExpirationDate,
          campaignIncentive: incomingCampaignIncentive,
          campaignCode: incomingCampaignCode,
        },
        quote: quoteFromState,

        requiresRecalculation: hasSfCampaignChanged(),
        hasCalculated: !hasSfCampaignChanged(),
        requiresQuoteResubmission: state.metadata?.submittedToSalesforce
          ? haveSfValuesChanged()
          : false,
      };

    case "QUOTE_UPDATED":
      if (!action.payload.quote || !state.username) {
        return { ...state };
      }

      const salesforceIntegration = {
        sfaccountid: state.sfIntegration?.sfaccountid,
        sfdealid: state.sfIntegration?.sfdealid,
        campaignId: state.sfIntegration?.campaignId,
        campaignName: state.sfIntegration?.campaignName,
        campaignExpirationDate: state.sfIntegration?.campaignExpirationDate,
        campaignIncentive: state.sfIntegration?.campaignIncentive,
        campaignCode: state.sfIntegration?.campaignCode,
      };

      return {
        ...state,
        quote: {
          ...action.payload.quote,
          ...salesforceIntegration,
          username: state.username,
        },
      };

    case "QUOTE_SAVING":
      return { ...state, isLoading: true };

    case "QUOTE_SAVED":
      return {
        ...state,
        quote: action.payload.quote,
        quoteCalculation: action.payload.calculation,
        hasCalculated: true,
        isLoading: false,
      };

    case "QUOTE_SAVE_ERROR":
      return {
        ...state,
        isLoading: false,
        isCalculating: false,
        hasCalculated: false,
      };

    case "QUOTE_SUBMITTING":
      return { ...state, isSubmitting: true };

    case "QUOTE_SUBMITTED":
      let submittedQuote = state.quote;

      return {
        ...state,
        quote: submittedQuote,
        hasQuoteBeenSubmitted: true,
        isSubmitting: false,
      };

    case "QUOTE_SUBMITTED_ERROR":
      return { ...state, hasQuoteBeenSubmitted: false, isSubmitting: false };

    case "QUOTE_CALCULATION_RESET":
      return { ...state, isCalculating: false, hasCalculated: false };

    case "QUOTE_CALCULATING":
      return { ...state, isCalculating: true, hasCalculationErrors: true };

    case "QUOTE_CALCULATED":
      let calculation = action.payload.calculation;
      let calcQuote = state.quote as FixAndFlipQuote;

      if (state.quoteType === "STF") {
        const fixNFlipCalc = calculation as FixAndFlipCalculation;
        const stfQuote = calcQuote as FixAndFlipQuote;

        fixNFlipCalc.LoanCosts.appraisalFee =
          stfQuote.loanTerm?.appraisalFee ?? 0;
        fixNFlipCalc.LoanCosts.brokerProcessingFee =
          stfQuote.loanTerm?.brokerProcessingFee ?? 0;

        calculation = fixNFlipCalc;
        calcQuote = stfQuote;
      }

      return {
        ...state,
        quote: calcQuote,
        quoteCalculation: calculation,
        isCalculating: false,
        hasCalculated: action.payload.hasCalculated,
        requiresRecalculation: false,
        calculationWarnings: undefined,
      };

    case "QUOTE_CALCULATE_WARNING":
      return {
        ...state,
        calculationWarnings: action.payload,
        hasCalculationErrors: !!action.payload.calculation,
      };

    case "QUOTE_CALCULATE_ERROR":
      return {
        ...state,
        isCalculating: false,
        isLoading: false,
        hasCalculated: false,
      };

    case "SCENARIO_LOADED":
      return {
        ...state,
        rentalQuoteScenarios: action.payload,
      };

    case "SCENARIO_UPDATE":
      const scenarios = [...state.rentalQuoteScenarios];
      if (action.payload.preferred === YesNo.Yes) {
        scenarios.forEach((x) => {
          x.preferred = YesNo.No;
        });
      }

      scenarios.splice(action.payload.scenarioId - 1, 1, action.payload);

      const quote: RentalQuote = {
        ...(state.quote as RentalQuote),
      };

      const originalRentalScenarios = quote.scenario ?? [];

      const areRentalScenariosEqual = originalRentalScenarios.every(
        (x, index) => x === scenarios[index]
      );

      return {
        ...state,
        quote: quote,
        rentalQuoteScenarios: [...scenarios],
        requiresRecalculation: !areRentalScenariosEqual,
        hasCalculated: areRentalScenariosEqual,
      };

    default:
      return { ...state };
  }
};
